import { useState, useRef, useEffect, useContext } from "react";
import axios from "axios";
import { Gamepad2, X, Minimize2, Maximize2, GripHorizontal, ChevronDown, Coins, RefreshCw, Zap, Users, Clock } from "lucide-react";
import { API, AuthContext } from "../App";
import DraggableResizableWindow from "./DraggableResizableWindow";
import { toast } from "sonner";

// Import games
import PoolGame from "./PoolGame";
import MultiplayerPoker from "./MultiplayerPoker";
import BlackjackGame from "./BlackjackGame";
import BowlingGame from "./BowlingGame";
import HangmanGame from "./HangmanGame";
import { FiveCardDraw, FiveCardStud, SevenCardStud } from "./CardPokerGames";
import StripPoker from "./StripPoker";
import RouletteGame from "./RouletteGame";
import ChessGame from "./ChessGame";
import SimonSaysGame from "./SimonSaysGame";
import TruthOrDareGame from "./TruthOrDareGame";
import DiceGames from "./DiceGames";

// Game definitions for the dropdown
const GAMES = [
  { id: "pool", name: "🎱 8-Ball Pool", icon: "🎱", category: "bar" },
  { id: "poker", name: "🃏 Texas Hold'em", icon: "🃏", category: "casino" },
  { id: "draw_poker", name: "🃏 5-Card Draw", icon: "🎴", category: "casino" },
  { id: "five_stud", name: "🃏 5-Card Stud", icon: "🎴", category: "casino" },
  { id: "seven_stud", name: "🃏 7-Card Stud", icon: "🎴", category: "casino" },
  { id: "blackjack", name: "🃏 Blackjack 21", icon: "🃏", category: "casino" },
  { id: "roulette", name: "🎰 Roulette", icon: "🎰", category: "casino" },
  { id: "dice_games", name: "🎲 Dice Games", icon: "🎲", category: "casino" },
  { id: "bowling", name: "🎳 10-Pin Bowling", icon: "🎳", category: "sports" },
  { id: "hangman", name: "🎯 Hangman", icon: "🎯", category: "word" },
  { id: "chess", name: "♔ Chess", icon: "♔", category: "strategy" },
  { id: "simon_says", name: "🎯 Simon Says", icon: "🎯", category: "memory" },
  { id: "truth_or_dare", name: "🎭 Truth or Dare", icon: "🎭", category: "party" },
];

// Adult-only games (only shown in adult rooms)
const ADULT_GAMES = [
  { id: "strip_poker", name: "💋 Strip Poker", icon: "💋", category: "adult", adult: true },
  { id: "truth_or_dare_adult", name: "🔥 Truth or Dare 18+", icon: "🔥", category: "adult", adult: true },
];

// Floating Game Window Component
const FloatingGameWindow = ({ gameId, game, currentUser, user, token, sendGameMessage, onClose, initialPosition, friends, roomId }) => {
  const getGameComponent = () => {
    const props = { currentUser, user, token, API, sendGameMessage, friends, roomId };
    switch (gameId) {
      case "pool": return <PoolGame {...props} />;
      case "poker": return <MultiplayerPoker {...props} />;
      case "draw_poker": return <FiveCardDraw {...props} onClose={onClose} />;
      case "five_stud": return <FiveCardStud {...props} onClose={onClose} />;
      case "seven_stud": return <SevenCardStud {...props} onClose={onClose} />;
      case "blackjack": return <BlackjackGame {...props} onClose={onClose} />;
      case "bowling": return <BowlingGame {...props} onClose={onClose} />;
      case "hangman": return <HangmanGame {...props} onClose={onClose} />;
      case "strip_poker": return <StripPoker {...props} onClose={onClose} />;      case "roulette": return <RouletteGame {...props} onClose={onClose} />;
      case "chess": return <ChessGame {...props} onClose={onClose} />;
      case "simon_says": return <SimonSaysGame {...props} onClose={onClose} />;
      case "truth_or_dare": return <TruthOrDareGame {...props} onClose={onClose} isAdult={false} />;
      case "truth_or_dare_adult": return <TruthOrDareGame {...props} onClose={onClose} isAdult={true} />;
      case "dice_games": return <DiceGames {...props} onClose={onClose} />;
      default: return <div>Game not found</div>;
    }
  };

  const getGameSize = () => {
    // Sizes that fit chat + game + friends side by side
    switch (gameId) {
      case "pool":
        return { width: 850, height: 500 };  // Bigger pool table
      case "poker":
        return { width: 800, height: 520 };
      case "draw_poker":
        return { width: 650, height: 500 };  // Larger for better visibility
      case "five_stud":
        return { width: 650, height: 500 };
      case "seven_stud":
        return { width: 700, height: 550 };
      case "blackjack":
        return { width: 600, height: 550 };  // Slightly larger
      case "bowling":
        return { width: 550, height: 500 };
      case "hangman":
        return { width: 550, height: 600 };
      case "strip_poker":
        return { width: 650, height: 550 };
      case "roulette":
        return { width: 750, height: 500 };
      case "chess":
        return { width: 700, height: 550 };
      case "simon_says":
        return { width: 500, height: 500 };
      case "truth_or_dare":
      case "truth_or_dare_adult":
        return { width: 650, height: 500 };
      case "dice_games":
        return { width: 550, height: 550 };
      default:
        return { width: 500, height: 450 };
    }
  };

  const size = getGameSize();

  return (
    <DraggableResizableWindow
      title={<><span className="mr-1">{game.icon}</span> {game.name}</>}
      onClose={onClose}
      initialPosition={initialPosition}
      initialSize={size}
      minSize={{ width: 350, height: 300 }}
      maxSize={{ width: 1000, height: 800 }}
      zIndex={5000}
      titleBarColor="bg-gradient-to-r from-[#7B0099] to-[#9B00BB]"
    >
      <div className="p-2 h-full overflow-auto retro-scrollbar bg-[#E8E8E8]">
        {getGameComponent()}
      </div>
    </DraggableResizableWindow>
  );
};

// Main Draggable Game Selector Component (sits in left panel)
const DraggableGameSelector = ({ roomId, currentUser, sendGameMessage, position = "left", friends = [] }) => {
  const { token, user } = useContext(AuthContext);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [openGames, setOpenGames] = useState([]); // Array of {gameId, position}
  const [fchipsBalance, setFchipsBalance] = useState(0);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [activeGames, setActiveGames] = useState([]);
  const [loadingActiveGames, setLoadingActiveGames] = useState(false);
  const [showQuickJoin, setShowQuickJoin] = useState(true);
  const dropdownRef = useRef(null);

  // Check if this is a game room (includes adult casino and pool hall)
  const isGameRoom = roomId === "the-game-room" || roomId === "game-room-1" || roomId === "game-room-2" || roomId === "game-room-3" || roomId === "adult-casino" || roomId === "adult-pool-hall" || roomId === "gaming" || roomId === "staff-members" || roomId === "staff-room";
  
  // Check if this is an adult room (shows adult games)
  const isAdultRoom = roomId === "adult-casino" || roomId === "adult-pool-hall" || roomId?.startsWith("adult-");
  
  // Get available games based on room type
  const availableGames = isAdultRoom ? [...GAMES, ...ADULT_GAMES] : GAMES;
  
  // Fetch active games for Quick Join
  const fetchActiveGames = async () => {
    if (!token) return;
    setLoadingActiveGames(true);
    try {
      const response = await axios.get(`${API}/games/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActiveGames(response.data.games || []);
    } catch (error) {
      console.error("Failed to fetch active games:", error);
    } finally {
      setLoadingActiveGames(false);
    }
  };

  // Fetch F-Chips balance
  useEffect(() => {
    if (!isGameRoom) return;
    const fetchBalance = async () => {
      if (!token) return;
      setLoadingBalance(true);
      try {
        const response = await axios.get(`${API}/fchips/balance`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFchipsBalance(response.data.balance || 0);
      } catch (error) {
        console.error("Failed to fetch F-Chips balance:", error);
      } finally {
        setLoadingBalance(false);
      }
    };
    fetchBalance();
    fetchActiveGames();
    
    // Update the F-Chips display instantly when a game (e.g. Strip Poker) settles a wager/pot.
    const onFchips = (e) => {
      if (typeof e.detail === "number") setFchipsBalance(e.detail);
      else fetchBalance();
    };
    window.addEventListener("fchips-updated", onFchips);
    
    // Auto-refresh active games every 30 seconds
    const interval = setInterval(fetchActiveGames, 30000);
    return () => { clearInterval(interval); window.removeEventListener("fchips-updated", onFchips); };
  }, [token, isGameRoom]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!isGameRoom) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isGameRoom]);

  if (!isGameRoom) return null;

  const openGame = (gameId) => {
    // Check if game is already open
    if (openGames.find(g => g.gameId === gameId)) {
      // Focus existing game by bringing to front (handled by DraggableResizableWindow)
      return;
    }

    // Calculate position for new window - stagger them
    const offset = openGames.length * 30;
    const newPosition = { 
      x: 250 + offset, 
      y: 100 + offset 
    };

    setOpenGames(prev => [...prev, { gameId, position: newPosition }]);
    setIsDropdownOpen(false);
  };

  const closeGame = (gameId) => {
    setOpenGames(prev => prev.filter(g => g.gameId !== gameId));
  };

  const closeAllGames = () => {
    setOpenGames([]);
  };

  const refreshBalance = async () => {
    if (!token) return;
    setLoadingBalance(true);
    try {
      const response = await axios.get(`${API}/fchips/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFchipsBalance(response.data.balance || 0);
    } catch (error) {
      console.error("Failed to fetch F-Chips balance:", error);
    } finally {
      setLoadingBalance(false);
    }
  };

  return (
    <>
      {/* Game Selector Panel - sits in the left sidebar area */}
      <div className="retro-window mb-2" data-testid="game-selector-panel">
        {/* Header */}
        <div className="retro-title-bar flex items-center justify-between px-2">
          <span className="flex items-center gap-1">
            <Gamepad2 className="w-4 h-4" /> 
            <span className="text-xs font-bold">🎮 GAMES</span>
          </span>
          {openGames.length > 0 && (
            <button 
              className="text-[10px] hover:bg-red-500 px-1 rounded"
              onClick={closeAllGames}
              title="Close all games"
            >
              ✕ All
            </button>
          )}
        </div>

        <div className="p-2 bg-[#C0C0C0]">
          {/* F-Chips Balance Display */}
          <div className="retro-inset p-2 mb-2 flex items-center justify-between bg-gradient-to-r from-green-50 to-emerald-50">
            <div className="flex items-center gap-1">
              <Coins className="w-4 h-4 text-green-600" />
              <span className="text-xs font-bold text-green-700">F-CHIPS:</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-sm font-bold text-green-800">
                {loadingBalance ? "..." : fchipsBalance.toLocaleString()}
              </span>
              <button 
                className="hover:bg-green-200 p-0.5 rounded"
                onClick={refreshBalance}
                disabled={loadingBalance}
                title="Refresh balance"
              >
                <RefreshCw className={`w-3 h-3 text-green-600 ${loadingBalance ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Game Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              className="retro-button w-full py-2 px-3 flex items-center justify-between text-sm font-bold bg-gradient-to-r from-purple-100 to-indigo-100 hover:from-purple-200 hover:to-indigo-200"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              data-testid="game-dropdown-btn"
            >
              <span className="flex items-center gap-2">
                <Gamepad2 className="w-4 h-4 text-[#7B0099]" />
                Select a Game
              </span>
              <ChevronDown className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown Menu - opens upward to prevent off-screen */}
            {isDropdownOpen && (
              <div className="absolute bottom-full left-0 right-0 mb-1 retro-window z-50 max-h-64 overflow-y-auto retro-scrollbar shadow-xl">
                <div className="p-1 bg-white">
                  {/* All games - simplified menu */}
                  {availableGames.map(game => (
                    <button
                      key={game.id}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-purple-100 flex items-center gap-2 ${
                        openGames.find(g => g.gameId === game.id) ? 'bg-green-100' : ''
                      } ${game.adult ? 'bg-pink-50 hover:bg-pink-100' : ''}`}
                      onClick={() => openGame(game.id)}
                    >
                      <span className="text-xl">{game.icon}</span>
                      <span className="font-bold">{game.name}</span>
                      {game.adult && <span className="text-[9px] bg-red-500 text-white px-1 rounded ml-auto">18+</span>}
                      {openGames.find(g => g.gameId === game.id) && (
                        <span className="ml-auto text-green-600 text-xs font-bold">OPEN</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Open Games List */}
          {openGames.length > 0 && (
            <div className="mt-2 retro-inset p-1">
              <div className="text-[10px] font-bold text-gray-600 mb-1">Open Games ({openGames.length}):</div>
              <div className="flex flex-wrap gap-1">
                {openGames.map(({ gameId }) => {
                  const game = [...GAMES, ...ADULT_GAMES].find(g => g.id === gameId);
                  return (
                    <div 
                      key={gameId} 
                      className={`retro-button text-[10px] px-1.5 py-0.5 flex items-center gap-1 ${game?.adult ? 'bg-pink-100' : 'bg-green-100'}`}
                    >
                      <span>{game?.icon}</span>
                      <button 
                        className="hover:text-red-600"
                        onClick={() => closeGame(gameId)}
                        title={`Close ${game?.name}`}
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Quick Join Panel */}
          <div className="mt-2">
            <button 
              onClick={() => setShowQuickJoin(!showQuickJoin)}
              className="w-full retro-button text-[10px] py-1 flex items-center justify-between px-2 bg-gradient-to-r from-yellow-100 to-orange-100 hover:from-yellow-200 hover:to-orange-200"
              data-testid="quick-join-toggle"
            >
              <span className="flex items-center gap-1">
                <Zap className="w-3 h-3 text-orange-500" />
                <span className="font-bold">QUICK JOIN</span>
                {activeGames.length > 0 && (
                  <span className="bg-red-500 text-white text-[8px] px-1 rounded-full animate-pulse">{activeGames.length}</span>
                )}
              </span>
              <ChevronDown className={`w-3 h-3 transition-transform ${showQuickJoin ? 'rotate-180' : ''}`} />
            </button>
            
            {showQuickJoin && (
              <div className="retro-inset p-1.5 mt-1 max-h-40 overflow-y-auto retro-scrollbar">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[9px] text-gray-500">Active tables across all rooms</span>
                  <button 
                    onClick={fetchActiveGames}
                    disabled={loadingActiveGames}
                    className="text-[9px] text-blue-500 hover:text-blue-700 flex items-center gap-0.5"
                  >
                    <RefreshCw className={`w-2.5 h-2.5 ${loadingActiveGames ? 'animate-spin' : ''}`} />
                  </button>
                </div>
                
                {activeGames.length === 0 ? (
                  <div className="text-center py-2 text-gray-400 text-[10px]">
                    <Gamepad2 className="w-4 h-4 mx-auto mb-1 opacity-50" />
                    No active games right now
                  </div>
                ) : (
                  <div className="space-y-1">
                    {activeGames.slice(0, 4).map((game, idx) => (
                      <div
                        key={`${game.game_type}-${game.code}-${idx}`}
                        className="retro-button p-1 flex items-center justify-between text-[10px] hover:bg-yellow-50"
                        data-testid={`quick-join-game-${game.code}`}
                      >
                        <div className="flex items-center gap-1 flex-1 min-w-0">
                          <span>{game.icon}</span>
                          <div className="truncate">
                            <span className="font-bold">{game.game_name}</span>
                            <div className="text-[8px] text-gray-500 flex items-center gap-1">
                              <Users className="w-2.5 h-2.5" />
                              {game.players}/{game.max_players}
                              <span className="truncate">by {game.host}</span>
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            toast.success(`Join code: ${game.code}`, {
                              description: `Open ${game.game_name} and enter this code!`
                            });
                          }}
                          className="retro-button text-[8px] px-1.5 py-0.5 bg-green-500 text-white hover:bg-green-600 ml-1"
                        >
                          JOIN
                        </button>
                      </div>
                    ))}
                    {activeGames.length > 4 && (
                      <div className="text-center text-[9px] text-gray-400">
                        +{activeGames.length - 4} more games
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Quick Tips */}
          <div className="mt-2 text-[9px] text-gray-500 text-center">
            💡 Games open in draggable windows!
            <br />
            You can play and chat at the same time.
          </div>
        </div>
      </div>

      {/* Render all open game windows */}
      {openGames.map(({ gameId, position }) => {
        const game = [...GAMES, ...ADULT_GAMES].find(g => g.id === gameId);
        return (
          <FloatingGameWindow
            key={gameId}
            gameId={gameId}
            game={game}
            currentUser={currentUser}
            user={user}
            token={token}
            sendGameMessage={sendGameMessage}
            onClose={() => closeGame(gameId)}
            initialPosition={position}
            friends={friends}
            roomId={roomId}
          />
        );
      })}
    </>
  );
};

export default DraggableGameSelector;
